import React, { createContext, useContext, useReducer, useEffect } from 'react';

const TestContext = createContext();

const initialState = {
  interventionId: null,
  patientInfo: {
    name: '',
    age: '',
    bloodPressure: '',
  },
  currentTestItem: 0,
  testItems: [
    {
      id: 1,
      title: "Test des bras",
      description: "Le patient doit lever les bras à 45° et les maintenir pendant 10 secondes",
      instruction: "Levez vos bras à 45° et maintenez cette position pendant 10 secondes",
      duration: 10,
      type: "arms",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 2,
      title: "Transition",
      description: "Approchez-vous du patient pour cadrer uniquement son visage",
      instruction: "Approchez-vous et cadrez uniquement le visage du patient",
      duration: 3,
      type: "transition",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 3,
      title: "Sourire",
      description: "Le patient doit donner son meilleur sourire puis revenir au repos",
      instruction: "Donnez votre meilleur sourire, puis revenez au repos",
      duration: 5,
      type: "smile",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 4,
      title: "Ouverture des yeux",
      description: "Le patient doit fermer puis ouvrir les yeux",
      instruction: "Fermez les yeux, puis ouvrez-les à nouveau",
      duration: 3,
      type: "eyes",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 5,
      title: "Froncement des sourcils",
      description: "Le patient doit froncer les sourcils puis revenir au repos",
      instruction: "Froncez les sourcils, puis revenez au repos",
      duration: 3,
      type: "frown",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 6,
      title: "Souffler une bougie",
      description: "Le patient doit faire le geste de souffler une bougie",
      instruction: "Faites le geste de souffler une bougie",
      duration: 3,
      type: "blow",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 7,
      title: "Baiser de bébé",
      description: "Le patient doit faire le geste de faire un baiser à un bébé",
      instruction: "Faites le geste de faire un baiser à un bébé",
      duration: 3,
      type: "kiss",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 8,
      title: "Gonflement des joues",
      description: "Le patient doit gonfler les joues puis revenir au repos",
      instruction: "Gonflez vos joues, puis revenez au repos",
      duration: 3,
      type: "cheeks",
      videoRecorded: false,
      videoUrl: null,
    },
    {
      id: 9,
      title: "Répétition de mots",
      description: "Le patient doit répéter les mots : Maman, tic-tac, moitié-moitié, splash",
      instruction: "Répétez après moi : Maman, tic-tac, moitié-moitié, splash",
      duration: 8,
      type: "words",
      videoRecorded: false,
      videoUrl: null,
    },
  ],
  systemChecks: {
    camera: false,
    microphone: false,
    battery: true,
    storage: true,
    internet: true,
    environment: true,
  },
  isRecording: false,
  isTestComplete: false,
  uploadProgress: 0,
  language: 'fr',
};

const testReducer = (state, action) => {
  switch (action.type) {
    case 'SET_INTERVENTION_ID':
      return {
        ...state,
        interventionId: action.payload,
      };
    case 'SET_PATIENT_INFO':
      return {
        ...state,
        patientInfo: { ...state.patientInfo, ...action.payload },
      };
    case 'SET_CURRENT_TEST_ITEM':
      return {
        ...state,
        currentTestItem: action.payload,
      };
    case 'UPDATE_TEST_ITEM':
      return {
        ...state,
        testItems: state.testItems.map((item, index) =>
          index === action.payload.index
            ? { ...item, ...action.payload.updates }
            : item
        ),
      };
    case 'SET_SYSTEM_CHECK':
      return {
        ...state,
        systemChecks: { ...state.systemChecks, ...action.payload },
      };
    case 'SET_RECORDING':
      return {
        ...state,
        isRecording: action.payload,
      };
    case 'SET_TEST_COMPLETE':
      return {
        ...state,
        isTestComplete: action.payload,
      };
    case 'SET_UPLOAD_PROGRESS':
      return {
        ...state,
        uploadProgress: action.payload,
      };
    case 'SET_LANGUAGE':
      return {
        ...state,
        language: action.payload,
      };
    case 'RESET_TEST':
      return {
        ...initialState,
        interventionId: state.interventionId,
        patientInfo: state.patientInfo,
        language: state.language,
      };
    default:
      return state;
  }
};

export const TestProvider = ({ children }) => {
  const [state, dispatch] = useReducer(testReducer, initialState);

  // Simuler les vérifications système
  useEffect(() => {
    const checkSystem = async () => {
      // Simulation des vérifications
      setTimeout(() => {
        dispatch({
          type: 'SET_SYSTEM_CHECK',
          payload: {
            camera: true,
            microphone: true,
            battery: true,
            storage: true,
            internet: navigator.onLine,
            environment: true,
          },
        });
      }, 1000);
    };

    checkSystem();
  }, []);

  const value = {
    ...state,
    dispatch,
    actions: {
      setInterventionId: (id) => dispatch({ type: 'SET_INTERVENTION_ID', payload: id }),
      setPatientInfo: (info) => dispatch({ type: 'SET_PATIENT_INFO', payload: info }),
      setCurrentTestItem: (index) => dispatch({ type: 'SET_CURRENT_TEST_ITEM', payload: index }),
      updateTestItem: (index, updates) => dispatch({ type: 'UPDATE_TEST_ITEM', payload: { index, updates } }),
      setSystemCheck: (checks) => dispatch({ type: 'SET_SYSTEM_CHECK', payload: checks }),
      setRecording: (isRecording) => dispatch({ type: 'SET_RECORDING', payload: isRecording }),
      setTestComplete: (isComplete) => dispatch({ type: 'SET_TEST_COMPLETE', payload: isComplete }),
      setUploadProgress: (progress) => dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: progress }),
      setLanguage: (language) => dispatch({ type: 'SET_LANGUAGE', payload: language }),
      resetTest: () => dispatch({ type: 'RESET_TEST' }),
    },
  };

  return <TestContext.Provider value={value}>{children}</TestContext.Provider>;
};

export const useTest = () => {
  const context = useContext(TestContext);
  if (!context) {
    throw new Error('useTest must be used within a TestProvider');
  }
  return context;
}; 